import { find, isEmpty, isNil, values, without } from "lodash";
import { KYC_MARKER } from "../constant/markerConstant";
import { isTrust } from "./investor";
import { Permissions } from "./permissions";
import { AmlStatus } from "../constant/amlConstant";
import { evaluateBool } from "./evaluation";
import { getMarkersOfInvestor } from "../api/marker";
import restUtils from "core/utils/rest";
import { getQuestionConfig, LabelQuestionType } from "./kycQuestionConstant";
import { fetchAssociatedParties } from "../zstore/associatedPartyStore";
import { fetchEntityRoles } from "../zstore/entityRoleStore";
import { fetchInvestorKYC } from "../zstore/investorKyc";
import { assembleKycAnswerObject, fetchKycQuestions, updateAndSyncKycAnswers, QuestionItem } from "../zstore/kycQuestionStore";
import { fetchInvestorTrs } from "../zstore/investorTrsStore";



const AUTO_APPLY_MESSAGE = "Apply marker before All KYC information provided.";

// Local minimal types to satisfy TypeScript
interface EntityRole {
  type: string;
  minParty: number;
  maxParty: number;
}

interface AssociatedParty {
  role: string;
}


interface Marker {
  code: string;
  removed: boolean;
}

interface InvestorDetails {
  entityType: string;
}

interface KycCheckContext {
  existingKycAnswer: Record<string, any>;
  allKycQuestions: QuestionItem[];
  existingAssociatedParties: AssociatedParty[];
  newRoles: EntityRole[];
  existingMarkers: Marker[];
  investorKyc: any;
  investorDetails: InvestorDetails;
}

export function applyKYC(investorId: string, commentText?: string) {
  return restUtils.call(
    "PortalApi",
    `hubwise-api/aml/${investorId}/markers`,
    "POST",
    {
      comment: commentText || AUTO_APPLY_MESSAGE,
      groupCode: "",
      markerCode: KYC_MARKER
    }
  );
}

export function removeKYC(investorId: string) {
  return restUtils.call(
    "PortalApi",
    `hubwise-api/aml/${investorId}/markers/${KYC_MARKER}`,
    "DELETE",
  );
}

export function validateAssociatedParties(roles: EntityRole[], associatedParties: AssociatedParty[]) {
  return roles.filter((role: EntityRole) => {
    const { minParty, maxParty } = role;
    const partyNum = associatedParties.filter((party: AssociatedParty) => party.role === role.type).length;
    const satisfied = (minParty < 0 || partyNum >= minParty) && (maxParty < 0 || partyNum <= maxParty);
    return !satisfied;
  });
}

export function validateKycQuestions(
  answer: Record<string, any>,
  questions: QuestionItem[],
  questionConfigs: Record<string, { show?: any }>
) {
  const incompleteQuestions: QuestionItem[] = [];
  const filteredAnswer: Record<string, any> = {};
  questions.forEach((q: QuestionItem) => {
    // we don't need answer for label questions.
    if (LabelQuestionType.includes(q.type)) {
      return;
    }
    // stop evaluate if the question is optional
    const config = questionConfigs[q.id];
    const show = config?.show === undefined ? false : evaluateBool(config.show, answer);
    // question still appliable to new sub-category
    if (show) {
      // answer for this question is empty, this is incomplete question
      if (isNil(answer[q.id]) || isEmpty(answer[q.id])) {
        incompleteQuestions.push(q);
      } else {
        // we have answer for this question. keep it.
        filteredAnswer[q.id] = answer[q.id];
      }
    }
  });
  return [incompleteQuestions, filteredAnswer];
}


export function kycGlobalCheck(
  personId: string,
  { categoryCode, subCategoryCode }: { categoryCode: string; subCategoryCode: string },
  kycCheckContext: KycCheckContext
) {
  const {
    existingKycAnswer,
    allKycQuestions,
    existingAssociatedParties,
    newRoles,
    existingMarkers,
    investorKyc,
    investorDetails,
  } = kycCheckContext;
  const requests: Promise<any>[] = [];
  // -- check roles --
  const incompleteRoles = validateAssociatedParties(newRoles, existingAssociatedParties);
  const hasIncompleteRole = incompleteRoles.length > 0;
  // -- check questions --
  // prefill new category and subCategory code.
  existingKycAnswer["QT1"] = categoryCode;
  existingKycAnswer["QT2"] = subCategoryCode;
  const questionConfigs = getQuestionConfig(existingKycAnswer, {
    investorCategories: [],
    investorSubCategories: [],
    associatedParties: existingAssociatedParties,
    permissions: [],
  });
  const [incompleteQuestions, filteredAnswer] = validateKycQuestions(existingKycAnswer, allKycQuestions, questionConfigs);
  const hasIncompleteQuestion = incompleteQuestions.length > 0;
  if (hasIncompleteQuestion) {
    requests.push(updateAndSyncKycAnswers(personId, investorDetails.entityType, filteredAnswer, allKycQuestions, investorKyc));
  }
  // -- check markers --
  const kycMarker = find(existingMarkers, { code: KYC_MARKER, removed: false });
  const hasKycMarker = !isNil(kycMarker);
  // all completed, remove KYC marker
  if (hasKycMarker && (!hasIncompleteQuestion && !hasIncompleteRole)) {
    requests.push(removeKYC(personId));
  }
  if (!hasKycMarker && (hasIncompleteQuestion || hasIncompleteRole)) {
    requests.push(applyKYC(personId, "Missing organisation details."));
  }
  return Promise.all(requests);
}

export async function prepareKycCheckContext(personId: string, initialContext: Partial<KycCheckContext> = {}) {
  const {
    investorKyc,
    existingKycAnswer,
    allKycQuestions,
    existingAssociatedParties,
    newRoles,
    existingMarkers,
    investorDetails,
  } = initialContext;
  const contextRequests: Promise<any>[] = [];
  const contextFieldMissing = (value: any) => isNil(value) || isEmpty(value);
  // fetch investor details
  if (contextFieldMissing(investorDetails)) {
    initialContext.investorDetails = await restUtils.call("PortalApi", `hubwise-api/ims/investor/${personId}`, "GET");
  }
  if (contextFieldMissing(existingKycAnswer) || contextFieldMissing(allKycQuestions)) {
    contextRequests.push(
      Promise.all([
        fetchKycQuestions(personId),
        contextFieldMissing(investorKyc) ? fetchInvestorKYC(personId) : investorKyc,
        isTrust(initialContext.investorDetails?.entityType ?? "") ? fetchInvestorTrs(personId) : {}
      ]).then(([kycQuestionsAndAnswers, kycData, trsData]) => {
        const [questions, answers] = kycQuestionsAndAnswers;
        initialContext.allKycQuestions = questions;
        initialContext.investorKyc = kycData;
        const ans = assembleKycAnswerObject(initialContext.investorDetails?.entityType ?? "", answers, questions, kycData, trsData);
        initialContext.existingKycAnswer = ans;
      })
    );
  }
  // fetch associated parties
  if (contextFieldMissing(existingAssociatedParties)) {
    contextRequests.push(
      fetchAssociatedParties(personId).then((data) => {
        initialContext.existingAssociatedParties = data as AssociatedParty[];
      })
    );
  }
  if (contextFieldMissing(newRoles)) {
    contextRequests.push(
      fetchEntityRoles(personId).then((data) => {
        initialContext.newRoles = data.roles as EntityRole[];
      })
    );
  }
  if (contextFieldMissing(existingMarkers)) {
    contextRequests.push(
      getMarkersOfInvestor(personId, []).then((data) => {
        initialContext.existingMarkers = data as Marker[];
      })
    );
  }
  return Promise.all(contextRequests).then(() => {
    return initialContext as KycCheckContext;
  }).catch((err) => {
    console.error(err);
    throw err;
  });
}

export function checkCategoryEditable(
  permissions: Array<{ permissionId: string; grantedRevoked: string }>,
  amlStatus: (typeof AmlStatus)[keyof typeof AmlStatus]
) {
  const authorisePermission = find(permissions, { permissionId: Permissions, grantedRevoked: "G" });
  const hasPermission = Boolean(authorisePermission);
  // all statuses indicates the AML check is in progress
  // i.e. statuses other than UNC and NRQ
  const statusesInprogress = without(values(AmlStatus), AmlStatus.UNC, AmlStatus.NRQ);
  // allow edit subCategory if:
  // 1. has AML_Authorise permission
  // 2. AML check is NOT in progress
  return hasPermission || !statusesInprogress.includes(amlStatus);
}
